import { useState } from 'react';
import { avatarHue, initials } from '@fsv/core';
import './Avatar.css';

/**
 * The person's profile picture (0014), or their initials on a colour from
 * their id — the same slot next to every name, picture or not. Decorative:
 * the name stands next to it, so it is hidden from screen readers.
 */
export function Avatar({
  person,
  size = 24,
}: {
  person: { id: string; name: string; avatarUrl?: string | null | undefined };
  size?: 24 | 32 | 48 | 64 | 96;
}) {
  // A read link lives an hour; one that expired (a long-open page) falls back to the initials.
  const [broken, setBroken] = useState<string | null>(null);
  // A rounded square like the app icon and the cards (owner: „mit weichen Kanten“), not a circle.
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4), borderRadius: Math.round(size * 0.22) };
  if (person.avatarUrl && broken !== person.avatarUrl) {
    const url = person.avatarUrl;
    return (
      <img className="avatar" src={url} alt="" width={size} height={size} style={style} aria-hidden="true" loading="lazy" onError={() => setBroken(url)} />
    );
  }
  const hue = avatarHue(person.id);
  return (
    <span className="avatar avatar--initials" aria-hidden="true" style={{ ...style, background: `hsl(${hue} 42% 38%)` }}>
      {initials(person.name)}
    </span>
  );
}

/** A name with its picture in front, for lists and lines of text. */
export function PersonName({
  person,
  size = 24,
  strong = false,
}: {
  person: { id: string; name: string; avatarUrl?: string | null | undefined };
  size?: 24 | 32;
  strong?: boolean;
}) {
  return (
    <span className="person">
      <Avatar person={person} size={size} />
      {strong ? <strong>{person.name}</strong> : <span>{person.name}</span>}
    </span>
  );
}
